"use client";

import { ChevronRight, FileText } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactElement } from "react";
import { useHelpHref } from "./help-link-base";

export type HelpChildPage = {
  readonly page_id: string;
  readonly title: string;
  /** Direct children of this page; > 0 shows the green count. */
  readonly child_count: number;
};

/** Rows of child pages: title, plus the count when a row has pages of its own. No page descriptions. */
export function HelpChildPageList({
  org_slug,
  pages,
}: {
  readonly org_slug: string;
  readonly pages: readonly HelpChildPage[];
}): ReactElement {
  const t = useTranslations("public.helpChildPageList");
  const helpHref = useHelpHref(org_slug);
  return (
    <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60 bg-background">
      {pages.map((page) => (
        <li key={page.page_id}>
          <Link
            href={helpHref({ kind: "page", pageId: page.page_id })}
            prefetch={false}
            className="group flex items-center gap-3.5 px-4 py-3 outline-none transition-colors hover:bg-muted/[0.35] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500/30"
          >
            <span
              aria-hidden
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/60 text-primary/50"
            >
              <FileText className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14.5px] font-medium leading-snug text-primary/90 group-hover:text-primary">
                {page.title}
              </span>
              {page.child_count > 0 ? (
                <span className="mt-0.5 block truncate text-[12.5px] font-medium leading-5 text-action-700">
                  {t("seiten", { count: page.child_count })}
                </span>
              ) : null}
            </span>
            <ChevronRight
              aria-hidden
              className="h-4 w-4 shrink-0 text-primary/30 transition-transform group-hover:translate-x-0.5 group-hover:text-primary/60"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}
